import { create } from 'zustand';

/**
 * Join Flow Steps
 */
export type JoinStep = 'loading' | 'error' | 'success';

/**
 * Join State Interface
 */
interface JoinState {
  /** Current step in the join flow */
  currentStep: JoinStep;
  /** Error message if any */
  error: string | null;
  /** Loading state for API calls */
  isLoading: boolean;
}

/**
 * Join Actions Interface
 */
interface JoinActions {
  /** Set current step */
  setStep: (step: JoinStep) => void;
  /** Set error message and move to error step */
  setError: (error: string | null) => void;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Handle successful invitation acceptance */
  invitationAccepted: () => void;
  /** Handle failed invitation acceptance */
  invitationFailed: (error: string) => void;
  /** Reset store to initial state */
  reset: () => void;
}

/**
 * Join Store Type
 */
export interface JoinStore extends JoinState, JoinActions {}

/**
 * Initial state for join flow
 */
const initialState: JoinState = {
  currentStep: 'loading',
  error: null,
  isLoading: false,
};

/**
 * Join Store
 *
 * Manages state for the invitation acceptance flow (existing users).
 *
 * ## Flow
 * 1. User arrives at /join with query params
 * 2. If existingUser=false → redirect to /create-password
 * 3. If existingUser=true and not logged in → redirect to /login
 * 4. If existingUser=true and logged in:
 *    - Store starts in 'loading' step
 *    - On successful acceptance: moves to 'success' step
 *    - On failed acceptance: moves to 'error' step
 *    - Redirect to / on success
 *
 * @example
 * ```tsx
 * const { currentStep, error, invitationAccepted } = useJoinStore();
 *
 * // After API success
 * invitationAccepted();
 * ```
 *
 * @since 1.0.0
 * @category State/Stores
 */
export const useJoinStore = create<JoinStore>()((set) => ({
  // Initial state
  ...initialState,

  // Actions
  setStep: (step: JoinStep) => set({ currentStep: step }),

  setError: (error: string | null) => set({ error, currentStep: 'error' }),

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  invitationAccepted: () =>
    set({
      currentStep: 'success',
      error: null,
      isLoading: false,
    }),

  invitationFailed: (error: string) =>
    set({
      currentStep: 'error',
      error,
      isLoading: false,
    }),

  reset: () => set(initialState),
}));
